import { Constructor } from '@angular/cdk/table';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { map, takeUntil, tap } from 'rxjs/operators';
import { DeliveryTargetsService } from 'src/app/content-list-management/shared/services/cmsapi/delivery-targets.service';
import { IWebsiteUrl } from 'src/app/core/dtos/interfaces/i-website-url';
import { IDeliveryTarget } from 'src/app/core/models/i-delivery-target';
import { ExpandableCheckboxListItem } from 'src/app/core/models/i-expandable-checkbox-list-item';

export type WithWebsitesFormSectionType = {
  deliveryTargets$: Observable<ExpandableCheckboxListItem[]>;
  deliveryTargetsMetadata: IDeliveryTarget[];
  isWebsiteInitComplete$: Observable<boolean>;
  _initDeliveryTargets(
    getPreviousSelectedValues: () => string[] | null,
    getUrls?: () => IWebsiteUrl[] | null,
  ): void;
  _updateDeliveryTargets(
    getPreviousSelectedValues: () => string[] | null,
    getUrls?: () => IWebsiteUrl[] | null,
  ): void;
  _populateWebsitesToFormGroup(
    deliveryTargets: IDeliveryTarget[],
    getPreviousSelectedValues: () => string[] | null,
  ): void;
  _buildDeliveryTargetCodes(): string[];
  _initWebsitesValueChanges(
    getPreviousSelectedValues: () => string[] | null,
  ): void;
  _initWebsiteValidation(): ValidatorFn;
  shouldListenToWebsiteForm: boolean;
};

type WebsitesFormSectionRequerimentsBase = {
  form: FormGroup;
  FORM_PATHS: { WEBSITES: () => string };
  destroy$: Subject<boolean>;
  deliveryTargetsService: DeliveryTargetsService;
  formBuilder: FormBuilder;
};

export function WithWebsitesFormSection<
  TBase extends Constructor<WebsitesFormSectionRequerimentsBase>,
>(
  baseClass: TBase,
): {
  new (...args: any[]): WithWebsitesFormSectionType;
  prototype: WithWebsitesFormSectionType;
} & TBase {
  return class WebsiteFormSectionable extends baseClass {
    selectedWebsiteCodes: string[];
    deliveryTargets$!: Observable<ExpandableCheckboxListItem[]>;
    deliveryTargetsMetadata!: IDeliveryTarget[];
    shouldListenToWebsiteForm: boolean = true;

    private _isWebsiteInitComplete: BehaviorSubject<boolean> =
      new BehaviorSubject(false);

    isWebsiteInitComplete$ = this._isWebsiteInitComplete.asObservable();

    constructor(...args: any[]) {
      super(...args);
    }

    _initDeliveryTargets(
      getPreviousSelectedValues: () => string[] | null,
      getUrls: () => IWebsiteUrl[] | null,
    ): void {
      this.deliveryTargets$ = this.deliveryTargetsService.getFiltered$().pipe(
        map((deliveryTargets: IDeliveryTarget[]) => {
          this.deliveryTargetsMetadata = deliveryTargets;
          this._populateWebsitesToFormGroup(
            deliveryTargets,
            getPreviousSelectedValues,
          );
          this._isWebsiteInitComplete.next(true);
          return deliveryTargets.map((dt) => {
            let url = null;
            if (getUrls) {
              url = getUrls()?.find((p) => p.deliveryTargetCode === dt.code);
            }
            return new ExpandableCheckboxListItem(
              dt.code,
              dt.friendlyName,
              url ? url.url : null,
            );
          });
        }),
      );
    }

    _updateDeliveryTargets(
      getPreviousSelectedValues: () => string[] | null,
      getUrls: () => IWebsiteUrl[] | null,
    ): void {
      // TODO update existing form rather than overide - this is reinitialising the form not updating it
      this.shouldListenToWebsiteForm = false;
      this.deliveryTargets$ = this.deliveryTargetsService.getFiltered$().pipe(
        map((deliveryTargets: IDeliveryTarget[]) => {
          this.deliveryTargetsMetadata = deliveryTargets;
          this._populateWebsitesToFormGroup(
            deliveryTargets,
            getPreviousSelectedValues,
          );
          return deliveryTargets.map((dt) => {
            let url = null;
            if (getUrls) {
              url = getUrls()?.find((p) => p.deliveryTargetCode === dt.code);
            }
            return new ExpandableCheckboxListItem(
              dt.code,
              dt.friendlyName,
              url ? url.url : null,
            );
          });
        }),
        tap(() => (this.shouldListenToWebsiteForm = true)),
      );
    }

    _populateWebsitesToFormGroup(
      deliveryTargets: IDeliveryTarget[],
      getPreviousSelectedValues: () => string[] | null,
    ): void {
      deliveryTargets.forEach((dt) =>
        (this.form.get(this.FORM_PATHS.WEBSITES()) as FormGroup).addControl(
          dt.code,
          this.formBuilder.control(
            getPreviousSelectedValues()?.includes(dt.code),
          ),
        ),
      );
    }

    _buildDeliveryTargetCodes(): string[] {
      return Object.entries(
        (this.form.get(this.FORM_PATHS.WEBSITES()) as FormGroup).controls,
      )
        .filter(([_, control]) => control.value)
        .map(([deliveryTargetCode, _]) => deliveryTargetCode);
    }

    _initWebsitesValueChanges(
      getPreviousSelectedValues: () => string[] | null,
    ): void {
      this.form
        .get(this.FORM_PATHS.WEBSITES())
        .valueChanges.pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.selectedWebsiteCodes = Object.entries(
              this.form.get(this.FORM_PATHS.WEBSITES()).value,
            )
              .filter(([_, value]) => value)
              .map(([code, _]) => code);

            const hasDataAndIsBackToInitialState =
              getPreviousSelectedValues()?.every((ddtc) =>
                this.selectedWebsiteCodes.includes(ddtc),
              ) &&
              getPreviousSelectedValues()?.length ===
                this.selectedWebsiteCodes.length;

            const isEmptyAndIsBackToInitialState =
              !getPreviousSelectedValues()?.length &&
              !this.selectedWebsiteCodes.length;

            const isBackToInitialState =
              hasDataAndIsBackToInitialState || isEmptyAndIsBackToInitialState;

            if (isBackToInitialState) {
              Object.values(
                (this.form.get(this.FORM_PATHS.WEBSITES()) as FormGroup)
                  .controls,
              ).forEach((formControl) => {
                formControl.markAsPristine();
              });
            }
          },
        });
    }

    _initWebsiteValidation(): ValidatorFn {
      return (control: AbstractControl): ValidationErrors | null => {
        let websiteControl: FormGroup = control as FormGroup;

        const isAnyTrue = !Object.values(websiteControl.controls).some(
          (v) => v.value,
        );

        if (isAnyTrue) {
          return { isAnyTrue };
        }

        return null;
      };
    }
  };
}
